import retry from 'retry';
import store from '@/store';

/**
 * 封装支持指数退避的重试机制
 * @param {Function} asyncFn - 需要重试的异步函数（需返回Promise）
 * @param {Object} options - 重试配置选项
 * @param {number} [options.retries=3] - 最大重试次数
 * @param {number} [options.factor=2] - 退避因子（指数基数）
 * @param {number} [options.minTimeout=1000] - 最小重试间隔(ms)
 * @param {number} [options.maxTimeout=30000] - 最大重试间隔(ms)
 * @param {boolean} [options.randomize=true] - 是否添加随机抖动
 * @param {Function} [isRetryableError] - 判断错误是否可重试的函数
 * @returns {Promise} - 返回带重试逻辑的Promise
 */
function _withRetry(asyncFn, options = {}, isRetryableError = defaultErrorFilter) {
    const op = retry.operation({
        retries: options.retries ?? 3,
        factor: options.factor ?? 2,
        minTimeout: options.minTimeout ?? 1000,
        maxTimeout: options.maxTimeout ?? 30000,
        randomize: options.randomize ?? true
    });

    return new Promise((resolve, reject) => {
        // 核心重试执行逻辑
        op.attempt(async (currentAttempt) => {
            try {
                console.log('retry attempt: ', currentAttempt)
                console.log(store.state)
                asyncFn.bind(store.state.poolInstance)
                const result = await asyncFn();
                resolve(result);
            } catch (error) {
                // 不可重试错误立即终止
                if (!isRetryableError(error)) {
                    op.stop();
                    reject(new NonRetryableError(error.message, currentAttempt));
                    return;
                }

                // 可重试错误记录日志并继续
                console.warn(`Attempt ${currentAttempt} failed: ${error.message}`);
                if (op.retry(error)) return;

                // 重试耗尽后包装错误信息
                reject(new MaxRetriesError(error.message, currentAttempt));
            }
        });
    });
}

// 默认错误过滤器（可重写）
function defaultErrorFilter(error) {
    const retryableCodes = ['SERVER_ERROR', 'TIMEOUT', 'NETWORK_ERROR'];
    return retryableCodes.includes(error?.code);
}

// 自定义错误类型
class NonRetryableError extends Error {
    constructor(message, attempt) {
        super(`[Fatal] ${message} (Attempt: ${attempt})`);
        this.name = 'NonRetryableError';
    }
}

class MaxRetriesError extends Error {
    constructor(message, attempt) {
        super(`[MaxRetries] ${message} (Attempt: ${attempt})`);
        this.name = 'MaxRetriesError';
    }
}

export const withRetry = _withRetry;